import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import './profile.css';
import {
  FaUserCircle, FaEnvelope, FaBuilding, FaIdBadge,
  FaLock, FaCheck, FaStore, FaGoogle, FaCalendarCheck, FaCalendarTimes,
} from 'react-icons/fa';

function Profile({ user }) {
  const navigate = useNavigate();

  // Password reset
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError]     = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  // Google Calendar
  const [gcalConnected, setGcalConnected]       = useState(false);
  const [gcalLoading, setGcalLoading]           = useState(true);
  const [gcalActionLoading, setGcalActionLoading] = useState(false);
  const [gcalMessage, setGcalMessage]           = useState(null);

  const token      = localStorage.getItem('token');
  const authHeader = { Authorization: `Bearer ${token}` };
  const userType   = user?.role === 'admin' ? 'admin' : 'employee';

  useEffect(() => {
    if (!user) return;
    const fetchCalendarStatus = async () => {
      try {
        const { data } = await API.get('/auth/google-calendar/status', {
          params: { userType },
          headers: authHeader,
        });
        setGcalConnected(data.connected);
      } catch (err) {
        console.error('Failed to fetch calendar status:', err.message);
      } finally {
        setGcalLoading(false);
      }
    };
    fetchCalendarStatus();
  }, [user]);

  const handleCalendarConnect = async () => {
    setGcalActionLoading(true);
    setGcalMessage(null);
    try {
      const { data } = await API.get('/auth/google-calendar/auth-url', {
        params: { userType },
        headers: authHeader,
      });
      window.location.href = data.authUrl;
    } catch {
      setGcalMessage({ type: 'error', text: 'Failed to start authorization. Please try again.' });
      setGcalActionLoading(false);
    }
  };

  const handleCalendarDisconnect = async () => {
    setGcalActionLoading(true);
    setGcalMessage(null);
    try {
      await API.post('/auth/google-calendar/disconnect', { userType }, { headers: authHeader });
      setGcalConnected(false);
      setGcalMessage({ type: 'success', text: 'Google Calendar disconnected successfully.' });
    } catch {
      setGcalMessage({ type: 'error', text: 'Failed to disconnect. Please try again.' });
    } finally {
      setGcalActionLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError(''); setPwSuccess('');
    setPwLoading(true);
    try {
      await API.post('/auth/request-reset', { email: user.email });
      setPwSuccess('A password reset link has been sent to your email.');
    } catch (err) {
      setPwError(err.response?.data?.error || 'Failed to send reset email.');
    } finally {
      setPwLoading(false);
    }
  };

  if (!user) return null;

  const isAdmin     = user.role === 'admin';
  const userInitial = user.name?.[0]?.toUpperCase() || 'U';

  return (
    <div className="profile-page">
      <div className="profile-header">
        <h2>My Profile</h2>
        <p>View and manage your account information</p>
      </div>

      <div className="profile-grid">

        {/* ---- Identity card ---- */}
        <div className="profile-card profile-identity">
          <div className="profile-avatar-wrap">
            <div className="profile-avatar">{userInitial}</div>
            <div className="profile-avatar-badge">{isAdmin ? 'Admin' : 'Employee'}</div>
          </div>
          <h3 className="profile-name">{user.name}</h3>
          <p className="profile-email">{user.email}</p>

          <div className="profile-info-list">
            <div className="profile-info-row">
              <FaEnvelope className="profile-info-icon" />
              <div>
                <span className="profile-info-label">Email</span>
                <span className="profile-info-value">{user.email || '—'}</span>
              </div>
            </div>
            <div className="profile-info-row">
              <FaIdBadge className="profile-info-icon" />
              <div>
                <span className="profile-info-label">Role</span>
                <span className="profile-info-value">{isAdmin ? 'Administrator' : 'Employee'}</span>
              </div>
            </div>
            <div className="profile-info-row">
              <FaBuilding className="profile-info-icon" />
              <div>
                <span className="profile-info-label">{isAdmin ? 'Business ID' : 'Business'}</span>
                <span className="profile-info-value profile-id">
                  {isAdmin ? user.id : user.business_id}
                </span>
              </div>
            </div>
            {!isAdmin && (
              <div className="profile-info-row">
                <FaUserCircle className="profile-info-icon" />
                <div>
                  <span className="profile-info-label">Employee ID</span>
                  <span className="profile-info-value profile-id">{user.id}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ---- Business Settings card (admin only) ---- */}
        {isAdmin && (
          <div className="profile-card profile-business-card">
            <div className="profile-card-title">
              <FaStore className="profile-card-title-icon" />
              <h4>Business Settings</h4>
            </div>
            <p className="profile-card-desc">
              Update your business profile, cover image, address, working hours, and owner information.
            </p>
            <button
              className="profile-btn-primary"
              onClick={() => navigate('/ubd')}
            >
              <FaStore size={14} /> Update Business Details
            </button>
          </div>
        )}

        {/* ---- Google Calendar card ---- */}
        <div className="profile-card profile-gcal-card">
          <div className="profile-card-title">
            <FaGoogle className="profile-card-title-icon profile-card-title-icon--google" />
            <h4>Google Calendar</h4>
          </div>
          <p className="profile-card-desc">
            When connected, every new booking is automatically added to your Google Calendar as an event.
          </p>

          {gcalMessage && (
            <div className={`profile-alert profile-alert--${gcalMessage.type}`} style={{ marginBottom: 14 }}>
              {gcalMessage.type === 'success' && <FaCheck />}
              {gcalMessage.text}
              <button className="profile-alert-close" onClick={() => setGcalMessage(null)}>✕</button>
            </div>
          )}

          <div className="profile-gcal-row">
            <div className="profile-gcal-status">
              {gcalLoading ? (
                <span className="profile-badge profile-badge--loading">Checking…</span>
              ) : gcalConnected ? (
                <span className="profile-badge profile-badge--connected">
                  <FaCalendarCheck /> Connected
                </span>
              ) : (
                <span className="profile-badge profile-badge--off">
                  <FaCalendarTimes /> Not connected
                </span>
              )}
            </div>

            {!gcalLoading && (
              gcalConnected ? (
                <button
                  className="profile-btn-danger"
                  onClick={handleCalendarDisconnect}
                  disabled={gcalActionLoading}
                >
                  {gcalActionLoading ? 'Disconnecting…' : 'Disconnect'}
                </button>
              ) : (
                <button
                  className="profile-btn-google"
                  onClick={handleCalendarConnect}
                  disabled={gcalActionLoading}
                >
                  <FaGoogle size={13} />
                  {gcalActionLoading ? 'Redirecting…' : 'Connect Google Calendar'}
                </button>
              )
            )}
          </div>

          <p className="profile-gcal-note">
            Connecting only grants permission to <strong>create events</strong>. Your existing calendar data is never read or modified.
          </p>
        </div>

        {/* ---- Password / security card ---- */}
        <div className="profile-card profile-security">
          <div className="profile-card-title">
            <FaLock className="profile-card-title-icon" />
            <h4>Password &amp; Security</h4>
          </div>
          <p className="profile-card-desc">
            Request a password reset link to be sent to your registered email address.
          </p>

          <form onSubmit={handleChangePassword} className="profile-pw-form">
            {pwError && <div className="profile-alert profile-alert--error">{pwError}</div>}
            {pwSuccess && (
              <div className="profile-alert profile-alert--success">
                <FaCheck /> {pwSuccess}
              </div>
            )}
            <button
              type="submit"
              className="profile-btn-primary"
              disabled={pwLoading}
            >
              {pwLoading ? 'Sending…' : 'Send Password Reset Email'}
            </button>
          </form>

          <div className="profile-divider" />

          <div className="profile-security-tips">
            <p className="profile-tips-title">Security tips</p>
            <ul>
              <li>Use at least 8 characters</li>
              <li>Mix letters, numbers and symbols</li>
              <li>Never share your password</li>
            </ul>
          </div>
        </div>

      </div>
    </div>
  );
}

export default Profile;
